import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useStructureContext } from 'context/StructureProvider';
import { storedAccessToken } from '../../services/http';
import {
  getScrubRange,
  getSimulationResults,
  getSimulations,
  getUpdatedChartDefinition,
  retryUnless404,
} from './helper';
import type { ResultData, Simulation } from './helper';

export type ContextType = {
  simulationIds: Array<number>;
  setSimulationIds: React.Dispatch<React.SetStateAction<Array<number>>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isSimulating: boolean;
  setIsSimulating: React.Dispatch<React.SetStateAction<boolean>>;
  timeIndex: number;
  setTimeIndex: React.Dispatch<React.SetStateAction<number>>;
  stopTime: Array<number>;
  startTime: Array<number>;
  outputInterval: Array<number>;
  invalidateCachedResponse: (id: number) => void;
  simulations: Array<Simulation> | [];
  charts: any;
  error?: any;
};

export const SimulationResultsContext: React.Context<ContextType | undefined> = React.createContext<
  ContextType | undefined
>(undefined);

const SimulationResultsProvider = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient();

  const { initialValues, system } = useStructureContext();
  const resultCharts = system?.result_charts || [];

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSimulating, setIsSimulating] = useState<boolean>(false);
  const [simulationIds, setSimulationIds] = useState<Array<number> | []>([]);
  const [simulations, setSimulations] = useState<Simulation[] | []>([]);
  const [stopTime, setStopTime] = useState<number[]>([1]);
  const [startTime] = useState<number[]>([0]);
  const [timeIndex, setTimeIndex] = useState<number>(0);
  const [outputInterval, setOutputInterval] = useState<number[]>([1]);

  // Fetch simulation data
  const { data: simulationsData, error: simulationsError } = useQuery({
    queryKey: ['Simulations', simulationIds],
    queryFn: () => (storedAccessToken() && simulationIds.length ? getSimulations(simulationIds) : null),
    retry: retryUnless404,
  });

  useEffect(() => {
    if (simulationsData && simulationsData !== null) {
      setOutputInterval(
        simulationsData.map((simulation) => (simulation?.output_interval ? simulation.output_interval : 1)),
      );
      setSimulations(
        simulationsData.map((simulation) => {
          const setupData = simulation.ui_setup_data ?? {};
          return {
            ...simulation,
            ui_setup_data: setupData,
            // test if it works as exepcted : if all ui_setup_data is not defined, it will use the parameters
            parameters: { ...setupData, initialValues },
          };
        }),
      );
    }
  }, [simulationsData]);

  // Fetch simulation results
  const { data: simulationResults, error: resultsError } = useQuery<ResultData[] | undefined>({
    queryKey: ['SimulationResults', simulationIds],
    queryFn: () => (storedAccessToken() && simulationIds.length ? getSimulationResults(simulationIds) : []),
    retry: retryUnless404,
  });

  useEffect(() => {
    setIsLoading(!simulations.length || !simulationResults?.length);
  }, [simulationResults, simulations.length]);

  // The scrub range is derived from the result data since the API no longer
  // exposes a simulation-level stop_time (times are per-element parameters).
  useEffect(() => {
    if (simulationsData && simulationResults?.length) {
      setStopTime(
        simulationsData.map(
          (simulation) =>
            getScrubRange(
              simulationResults.find((result) => result.simulationId === simulation.id),
              simulation.output_interval || 1,
            ) || 1,
        ),
      );
    }
  }, [simulationResults, simulationsData]);

  /**
   * @description Chart definitions (with data if available), enriched with necessary
   * configuration, ready to be rendered by the chart components
   */
  const charts = useMemo(
    () =>
      resultCharts?.flatMap((chartDefinition: any) =>
        getUpdatedChartDefinition(chartDefinition, simulations, simulationResults),
      ) ?? [],
    [simulationResults, simulations, resultCharts],
  );

  const invalidateCachedResponse = (id: number) => {
    queryClient.invalidateQueries({ queryKey: ['Simulations', id] });
    queryClient.invalidateQueries({ queryKey: ['SimulationResults', id] });
  };

  return (
    <SimulationResultsContext.Provider
      value={{
        simulationIds,
        setSimulationIds,
        isLoading,
        setIsLoading,
        isSimulating,
        setIsSimulating,
        timeIndex,
        setTimeIndex,
        stopTime,
        startTime,
        outputInterval,
        invalidateCachedResponse,
        simulations,
        charts,
        error: simulationsError ?? resultsError,
      }}
    >
      {children}
    </SimulationResultsContext.Provider>
  );
};

export const useSimulationResultsContext = (): ContextType => {
  const context = React.useContext(SimulationResultsContext);
  if (context === undefined) {
    throw new Error('useSimulationResultsContext must be used within a SimulationResultsProvider');
  }
  return context;
};

export default SimulationResultsProvider;
